import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getExamStatus } from 'services/ExamService';
import { getTechOverviewCollateral } from 'services/TechOverviewService';
import { Modality } from 'types';
import { ExamStatus } from 'types/exam';
import type { ExamStatusInfo, TechOverviewDuration } from '../../utils/types';

type UseCollateralInfoProps = {
  modality: Modality;
  sku?: string;
  slug?: string;
};

const useCollateralInfo = ({ modality, sku, slug }: UseCollateralInfoProps) => {
  const [examStatusInfo, setExamStatusInfo] = useState<ExamStatusInfo>({
    status: ExamStatus.Unenrolled,
  });
  const [techOverviewDuration, setTechOverviewDuration] =
    useState<TechOverviewDuration>({
      duration: 0,
      unit: 'minutes',
    });
  const { i18n, t } = useTranslation();

  const getExamStatusInfo = useCallback(async () => {
    try {
      if (sku) {
        const exam = await getExamStatus(
          sku,
          sku.toLowerCase().startsWith('xe'),
        );

        if (Object.keys(exam?.user_exam).length) {
          let content: string;
          const { user_exam: userExam } = exam;

          if (userExam?.status === ExamStatus.Unscheduled)
            content = t('Scheduling is available!');
          if (userExam?.status === ExamStatus.Scheduled)
            content = userExam?.exam_date;

          setExamStatusInfo({
            status: userExam?.status || ExamStatus.Unenrolled,
            content,
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [slug, setExamStatusInfo, t]);

  const getTechOverviewDuration = useCallback(async () => {
    try {
      if (slug) {
        const id = `${slug.replace('-', '@@')}@@${i18n.language}`;

        const collateral = await getTechOverviewCollateral(id);

        if (collateral) {
          setTechOverviewDuration({
            duration: collateral?.duration || 0,
            unit: collateral?.duration_unit || 'minutes',
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [slug, i18n.language, setTechOverviewDuration]);

  useEffect(() => {
    if (modality === Modality.Exam) {
      getExamStatusInfo();
    }

    if (modality === Modality.TechOverview) {
      getTechOverviewDuration();
    }
  }, [modality, getExamStatusInfo, getTechOverviewDuration]);

  return { examStatusInfo, techOverviewDuration };
};

export default useCollateralInfo;
